import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { TicketKey } from "@m2s/design-system";

import { api } from "@/lib/api";
import type { Issue, PageSummary, Space } from "@/lib/types";
import { useUIStore } from "@/store/ui";

export function CommandPalette() {
  const open = useUIStore((s) => s.paletteOpen);
  const setOpen = useUIStore((s) => s.setPaletteOpen);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setOpen]);

  const { data: issues = [] } = useQuery({
    queryKey: ["palette-issues"],
    queryFn: async () => (await api.get<Issue[]>("/issues?limit=100")).data,
    enabled: open,
  });

  const { data: spaces = [] } = useQuery({
    queryKey: ["palette-spaces"],
    queryFn: async () => (await api.get<Space[]>("/spaces")).data,
    enabled: open,
  });

  const { data: pages = [] } = useQuery({
    queryKey: ["palette-pages", spaces.map((s) => s.slug)],
    queryFn: async () => {
      if (spaces.length === 0) return [] as PageSummary[];
      const all = await Promise.all(
        spaces.map((sp) => api.get<PageSummary[]>(`/spaces/${sp.slug}/pages`)),
      );
      return all.flatMap((r) => r.data);
    },
    enabled: open && spaces.length > 0,
  });

  const q = query.toLowerCase().trim();

  const issueMatches = useMemo(() => {
    if (!q) return issues.slice(0, 5);
    return issues
      .filter(
        (i) =>
          i.key.toLowerCase().includes(q) ||
          i.title.toLowerCase().includes(q),
      )
      .slice(0, 6);
  }, [issues, q]);

  const pageMatches = useMemo(() => {
    if (!q) return pages.slice(0, 4);
    return pages.filter((p) => p.title.toLowerCase().includes(q)).slice(0, 5);
  }, [pages, q]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal
      onClick={() => setOpen(false)}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        backdropFilter: "blur(4px)",
        display: "grid",
        placeItems: "flex-start center",
        paddingTop: "10vh",
        zIndex: 100,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(640px, calc(100vw - 32px))",
          background: "var(--canvas)",
          border: "1px solid var(--hairline)",
          borderRadius: 8,
          boxShadow:
            "0 30px 18px rgba(0,0,0,0.06), 0 13px 13px rgba(0,0,0,0.10), 0 3px 7px rgba(0,0,0,0.12)",
          overflow: "hidden",
        }}
      >
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search issues, pages…"
          style={{
            width: "100%",
            padding: "14px 18px",
            border: "none",
            borderBottom: "1px solid var(--hairline)",
            background: "transparent",
            outline: "none",
            fontFamily: "var(--font-sans)",
            fontSize: 15,
            color: "var(--ink)",
          }}
        />
        <div style={{ maxHeight: 420, overflowY: "auto" }}>
          {issueMatches.length > 0 && (
            <Section title="Issues">
              {issueMatches.map((i) => (
                <button
                  key={i.id}
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    navigate(`/issues/${i.key}`);
                  }}
                  style={rowStyle}
                >
                  <TicketKey value={i.key} />
                  <span style={{ flex: 1, color: "var(--ink)", fontSize: 13.5 }}>
                    {i.title}
                  </span>
                  <span style={{ fontSize: 11, color: "var(--mute)" }}>{i.status}</span>
                </button>
              ))}
            </Section>
          )}
          {pageMatches.length > 0 && (
            <Section title="Pages">
              {pageMatches.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    navigate(`/wiki/pages/${p.id}`);
                  }}
                  style={rowStyle}
                >
                  <span
                    aria-hidden
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: 2,
                      background: "rgba(237,82,203,0.18)",
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ flex: 1, color: "var(--ink)", fontSize: 13.5 }}>
                    {p.title}
                  </span>
                </button>
              ))}
            </Section>
          )}
          {issueMatches.length === 0 && pageMatches.length === 0 && (
            <div
              style={{
                padding: "28px 18px",
                textAlign: "center",
                color: "var(--mute)",
                fontSize: 13,
              }}
            >
              Nothing matches "{query}".
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const rowStyle: React.CSSProperties = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "8px 18px",
  background: "transparent",
  border: "none",
  cursor: "pointer",
  textAlign: "left",
};

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ paddingBottom: 8 }}>
      <div
        style={{
          padding: "10px 18px 4px",
          fontSize: 10,
          fontWeight: 500,
          letterSpacing: 1.2,
          textTransform: "uppercase",
          color: "var(--mute)",
        }}
      >
        {title}
      </div>
      {children}
    </div>
  );
}
